import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { api, getToken } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import RichTextEditor from '../../components/RichTextEditor.jsx';
import { DOC_KINDS, Modal } from './ws.jsx';
import { EmailTemplates } from './WorkspaceMore.jsx';
import { SignaturesSection, SigningPanel } from './Signatures.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// "Documents" (migration 033): sub-menu, template library, letterhead.

export function DocsNav({ base = '/account/workspace' }) {
  const { t } = useI18n();
  const tabs = [[`${base}/documents`, 'sent', true], [`${base}/documents/new`, 'new'], [`${base}/templates`, 'templates', true], [`${base}/templates/letterhead`, 'letterhead']];
  return (
    <nav className="ws-tabs ws-doc-tabs" aria-label={t('ws.docs.menu')}>
      {tabs.map(([to, k, end]) => <NavLink key={k} to={to} end={end} className={({ isActive }) => (isActive ? 'active' : '')}>{t(`ws.docs.tab.${k}`)}</NavLink>)}
    </nav>
  );
}

// PDF (blob) in a frame, plus "open" for phones that can't show it inline.
export function PdfFrame({ url, title }) {
  const { t } = useI18n();
  if (!url) return null;
  return (
    <div className="ws-pdf">
      <iframe src={url} title={title} className="ws-pdf-frame" />
      <a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noreferrer">{t('ws.lib.openPdf')} ↗</a>
    </div>
  );
}
export async function pdfBlobUrl(path, body) {
  const r = await fetch(`/api${path}`, body ? { method: 'POST', headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    : { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) { let e = 'server_error'; try { e = (await r.json()).error || e; } catch { /* not JSON */ } throw Object.assign(new Error(e), { code: e }); }
  return URL.createObjectURL(await r.blob());
}

// Placeholder chips: a click types {name} where the cursor is in the editor.
export function PlaceholderChips({ list }) {
  const { t } = useI18n();
  const insert = (p) => { document.execCommand('insertText', false, `{${p}}`); };
  return (
    <div className="ws-chips" aria-label={t('ws.lib.placeholders')}>
      <span className="muted small">{t('ws.lib.placeholders')} :</span>
      {list.map((p) => <button key={p} type="button" className="ws-chip" title={t(`ws.ph.${p}`)} onMouseDown={(e) => e.preventDefault()} onClick={() => insert(p)}>{`{${p}}`}</button>)}
    </div>
  );
}

// ── Modèles / Templates ─────────────────────────────────────────────────
export function DocTemplates({ base = '/account/workspace' }) {
  const { t, lang, errorText } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('');
  const [tlang, setTlang] = useState('');
  const [archived, setArchived] = useState(false);
  const [edit, setEdit] = useState(null);          // { id?, kind, lang, title, bodyHtml, hasItems }
  const [preview, setPreview] = useState(null);    // { title, url }
  const [msg, setMsg] = useState({ kind: '', text: '' });
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api(`/workspace/doc-library${archived ? '?archived=1' : ''}`).then(setData).catch((e) => setMsg({ kind: 'error', text: errorText(e) })), [archived, errorText]);
  useEffect(() => { load(); }, [load]);
  const [stamps, setStamps] = useState([]);
  useEffect(() => { api('/workspace/doc-stamps').then((d) => setStamps(d.stamps)).catch(() => {}); }, []);
  const match = (x) => (!kind || x.kind === kind) && (!tlang || x.lang === tlang);

  async function showPreview(x) {
    setBusy(`p${x.id}`);
    try { setPreview({ title: x.title, url: await pdfBlobUrl('/workspace/doc-library/preview', { kind: x.kind, lang: x.lang, title: x.title, bodyHtml: x.bodyHtml, hasItems: x.hasItems }) }); }
    catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  async function duplicate(x) {
    setBusy(`d${x.id}`);
    try {
      const n = await api('/workspace/doc-library', { method: 'POST', body: { fromId: x.id, title: `${x.title} (${t('ws.lib.copy')})` } });
      await load(); setEdit(n); setMsg({ kind: 'success', text: t('ws.lib.duplicated') });
    } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  async function archive(x, on) {
    try { await api(`/workspace/doc-library/${x.id}/archive`, { method: 'POST', body: { archived: on } }); load(); setMsg({ kind: 'success', text: t(on ? 'ws.lib.archived' : 'ws.lib.restored') }); }
    catch (e) { setMsg({ kind: 'error', text: errorText(e) }); }
  }
  async function save() {
    setBusy('save'); setMsg({ kind: '', text: '' });
    try {
      const body = { kind: edit.kind, lang: edit.lang, title: edit.title, bodyHtml: edit.bodyHtml, hasItems: edit.hasItems, defaultStampId: edit.defaultStampId || null, defaultSigning: edit.defaultSigning || null };
      if (edit.id) await api(`/workspace/doc-library/${edit.id}`, { method: 'PUT', body }); else await api('/workspace/doc-library', { method: 'POST', body });
      setEdit(null); load(); setMsg({ kind: 'success', text: t('ws.lib.saved') });
    } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }

  if (!data) return <div className="ws-page"><DocsNav base={base} /><Alert kind={msg.kind || 'error'}>{msg.text}</Alert>{!msg.text && <p className="muted">{t('common.loading')}</p>}</div>;
  const row = (x) => (
    <li key={x.id} className={`ws-tpl${x.archived ? ' is-off' : ''}`}>
      <div className="ws-grow">
        <strong>{x.title}</strong>
        <span className="muted small"> · {t(`ws.kind.${x.kind}`)} · {x.lang.toUpperCase()}{x.hasItems ? ` · ${t('ws.lib.withItems')}` : ''}{x.createdBy ? ` · ${x.createdBy}` : ''}{x.archived ? ` · ${t('ws.lib.archivedTag')}` : ''}</span>
      </div>
      <div className="ws-row ws-tpl-actions">
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy === `p${x.id}`} onClick={() => showPreview(x)}>{t('ws.lib.preview')}</button>
        {data.canCreate && <button type="button" className="btn btn-ghost btn-sm" disabled={busy === `d${x.id}`} onClick={() => duplicate(x)}>{t('ws.lib.duplicate')}</button>}
        {!x.starter && x.canEdit && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEdit({ ...x })}>{t('ws.lib.edit')}</button>}
        {!x.starter && x.canEdit && <button type="button" className="btn btn-link btn-sm" onClick={() => archive(x, !x.archived)}>{t(x.archived ? 'ws.lib.restore' : 'ws.lib.archive')}</button>}
        {!x.archived && <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(`${base}/documents/new?template=${encodeURIComponent(x.id)}`)}>{t('ws.lib.use')}</button>}
      </div>
    </li>
  );
  const own = data.templates.filter(match);
  const starters = data.starters.filter(match);
  return (
    <div className="ws-page">
      <DocsNav base={base} />
      <div className="acc-head"><TutorialSlot place="ws.docs.templates" />
        <div><h1>{t('ws.lib.title')}</h1><p className="muted">{t('ws.lib.lead')}</p></div>
        {data.canCreate && !edit && <div className="acc-head-actions"><button type="button" className="btn btn-gold" onClick={() => setEdit({ kind: kind || 'letter', lang: tlang || lang, title: '', bodyHtml: '', hasItems: false })}>+ {t('ws.lib.new')}</button></div>}
      </div>
      <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>

      {edit && (
        <section className="ws-card ws-tpl-edit">
          <h2>{edit.id ? t('ws.lib.editTitle') : t('ws.lib.newTitle')}</h2>
          <div className="ws-form">
            <label className="field ws-full"><span className="field-label">{t('ws.lib.name')}</span><input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} maxLength={200} /></label>
            <label className="field"><span className="field-label">{t('ws.docs.kind')}</span>
              <select value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value, hasItems: edit.hasItems || data.withItems.includes(e.target.value) })}>{DOC_KINDS.map((k) => <option key={k} value={k}>{t(`ws.kind.${k}`)}</option>)}</select></label>
            <label className="field"><span className="field-label">{t('ws.docs.lang')}</span>
              <select value={edit.lang} onChange={(e) => setEdit({ ...edit, lang: e.target.value })}><option value="fr">Français</option><option value="en">English</option></select></label>
            <label className="field-checkbox ws-full"><input type="checkbox" checked={edit.hasItems} onChange={(e) => setEdit({ ...edit, hasItems: e.target.checked })} /><span>{t('ws.lib.hasItems')}</span></label>
            {stamps.length > 0 && (
              <label className="field ws-full"><span className="field-label">{t('ws.lib.defaultStamp')}</span>
                <select value={edit.defaultStampId || ''} onChange={(e) => setEdit({ ...edit, defaultStampId: Number(e.target.value) || null })}>
                  <option value="">{t('ws.lib.noDefaultStamp')}</option>{stamps.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></label>
            )}
          </div>
          <PlaceholderChips list={data.placeholders} />
          <RichTextEditor id="tpl-body" full docs docLang={edit.lang} value={edit.bodyHtml} onChange={(v) => setEdit((x) => ({ ...x, bodyHtml: v }))} placeholder={t('ws.lib.bodyPh')} onError={(c) => setMsg({ kind: 'error', text: t(`errors.${c}`) })} />
          <div className="ws-tpl-defaults"><SigningPanel template value={edit.defaultSigning || null} onChange={(v) => setEdit((x) => ({ ...x, defaultSigning: v }))} /></div>
          <p className="ws-hint">{t('ws.lib.bodyHint')}</p>
          <div className="ws-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setEdit(null)}>{t('common.cancel')}</button>
            <button type="button" className="btn btn-ghost" onClick={() => showPreview({ ...edit, id: 'edit' })}>{t('ws.lib.preview')}</button>
            <button type="button" className="btn btn-primary" disabled={busy === 'save'} onClick={save}>{busy === 'save' ? t('common.saving') : t('common.save')}</button>
          </div>
        </section>
      )}

      <div className="acc-toolbar">
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t('ws.docs.kind')}><option value="">{t('ws.docs.allKinds')}</option>{DOC_KINDS.map((k) => <option key={k} value={k}>{t(`ws.kind.${k}`)}</option>)}</select>
        <select value={tlang} onChange={(e) => setTlang(e.target.value)} aria-label={t('ws.docs.lang')}><option value="">{t('ws.docs.allLangs')}</option><option value="fr">FR</option><option value="en">EN</option></select>
        <label className="field-checkbox"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /><span>{t('ws.lib.showArchived')}</span></label>
      </div>
      <section className="ws-card">
        <h2>{t('ws.lib.mine')}</h2>
        {own.length ? <ul className="ws-list ws-tpl-list">{own.map(row)}</ul> : <p className="muted small">{t('ws.lib.none')}</p>}
      </section>
      <section className="ws-card">
        <h2>{t('ws.lib.starters')}</h2>
        <p className="ws-hint">{t('ws.lib.startersHint')}</p>
        <ul className="ws-list ws-tpl-list">{starters.map(row)}</ul>
      </section>
      <EmailTemplates canEdit={data.canManage} />
      {preview && <Modal title={preview.title} wide onClose={() => setPreview(null)}><PdfFrame url={preview.url} title={preview.title} /></Modal>}
      <p className="small"><Link to={`${base}/templates/letterhead`}>{t('ws.lh.title')} →</Link></p>
    </div>
  );
}

// ── Papier à en-tête / Letterhead ───────────────────────────────────────
const LH_FIELDS = [['companyName', 'full'], ['address', 'full'], ['phone'], ['email'], ['website'], ['regNumber'], ['footerText', 'full']];
export function DocLetterhead({ base = '/account/workspace' }) {
  const { t, lang, errorText } = useI18n();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [imgs, setImgs] = useState({});
  const [msg, setMsg] = useState({ kind: '', text: '' });
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const loadImg = (w) => fetch(`/api/workspace/doc-settings/${w}`, { headers: { Authorization: `Bearer ${getToken()}` } }).then((r) => (r.ok ? r.blob() : null)).then((b) => setImgs((x) => ({ ...x, [w]: b ? URL.createObjectURL(b) : null }))).catch(() => {});
  useEffect(() => {
    api('/workspace/doc-letterhead').then((d) => { setData(d); setF(d.letterhead); if (d.letterhead.hasLogo) loadImg('logo'); })
      .catch((e) => setMsg({ kind: 'error', text: errorText(e) }));
  }, [errorText]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const sample = useMemo(() => (lang === 'en'
    ? '<p><strong>Date:</strong> {date}<br><strong>Reference:</strong> {reference}</p><p><strong>{customer_company}</strong><br>{customer_address}</p><p>Dear Sir or Madam,</p><p>This is a preview of your letterhead: logo and contact details at the top of every page, footer at the bottom.</p><p>{items_table}</p><p>Yours faithfully,</p>'
    : '<p><strong>Date :</strong> {date}<br><strong>Référence :</strong> {reference}</p><p><strong>{customer_company}</strong><br>{customer_address}</p><p>Madame, Monsieur,</p><p>Voici l’aperçu de votre papier à en-tête : logo et coordonnées en haut de chaque page, pied de page en bas.</p><p>{items_table}</p><p>Veuillez agréer nos salutations distinguées.</p>'), [lang]);

  async function save(andPreview) {
    setBusy('save'); setMsg({ kind: '', text: '' });
    try {
      const r = await api('/workspace/doc-letterhead', { method: 'PUT', body: f });
      setF(r.letterhead); setMsg({ kind: 'success', text: t('ws.lh.saved') });
      if (andPreview) setPreview(await pdfBlobUrl('/workspace/doc-library/preview', { kind: 'quotation', lang, title: t('ws.lh.previewTitle'), bodyHtml: sample, hasItems: true }));
    } catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  async function upload(which, file) {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) { setMsg({ kind: 'error', text: t('errors.invalid_image') }); return; }
    const fd = new FormData(); fd.append('file', file);
    setBusy(which);
    try { await api(`/workspace/doc-settings/${which}`, { method: 'POST', form: fd }); setImgs((x) => ({ ...x, [which]: URL.createObjectURL(file) })); set(which === 'logo' ? 'hasLogo' : 'hasSignature', true); }
    catch (e) { setMsg({ kind: 'error', text: errorText(e) }); } finally { setBusy(''); }
  }
  async function removeImg(which) {
    try { await api(`/workspace/doc-settings/${which}`, { method: 'DELETE' }); setImgs((x) => ({ ...x, [which]: null })); set(which === 'logo' ? 'hasLogo' : 'hasSignature', false); }
    catch (e) { setMsg({ kind: 'error', text: errorText(e) }); }
  }

  if (!f) return <div className="ws-page"><DocsNav base={base} /><Alert kind={msg.kind || 'error'}>{msg.text}</Alert>{!msg.text && <p className="muted">{t('common.loading')}</p>}</div>;
  const ro = !data.canEdit;
  return (
    <div className="ws-page">
      <DocsNav base={base} />
      <div className="acc-head"><TutorialSlot place="ws.docs.letterhead" /><div><h1>{t('ws.lh.title')}</h1><p className="muted">{t('ws.lh.lead')}</p></div></div>
      {ro && <p className="callout callout-info small">{t('ws.lh.readOnly')}</p>}
      <div className="ws-cols">
        <section className="ws-card">
          <h2>{t('ws.lh.logo')}</h2>
          <div className="ws-row ws-lh-logo">
            {imgs.logo ? <img src={imgs.logo} alt="" /> : <span className="muted small">{t('ws.lh.noLogo')}</span>}
            {!ro && <label className="btn btn-ghost btn-sm">{busy === 'logo' ? t('common.loading') : t(f.hasLogo ? 'ws.lh.change' : 'ws.lh.upload')}<input type="file" accept="image/png,image/jpeg" hidden onChange={(e) => upload('logo', e.target.files[0])} /></label>}
            {!ro && f.hasLogo && <button type="button" className="btn btn-link btn-sm is-danger" onClick={() => removeImg('logo')}>{t('ws.lh.remove')}</button>}
          </div>
          <p className="ws-hint">{t('ws.lh.logoHint')}</p>
          <fieldset className="ws-inline" disabled={ro}>
            <legend className="field-label">{t('ws.lh.position')}</legend>
            {['left', 'center'].map((p) => <label key={p} className="field-checkbox"><input type="radio" name="lh-pos" checked={f.logoPosition === p} onChange={() => set('logoPosition', p)} /><span>{t(`ws.lh.pos.${p}`)}</span></label>)}
          </fieldset>
          <div className="ws-form">
            {LH_FIELDS.filter(([k]) => k !== 'footerText').map(([k, full]) => (
              <label key={k} className={`field${full ? ' ws-full' : ''}`}><span className="field-label">{t(`ws.lh.f.${k}`)}</span>
                <input value={f[k] || ''} onChange={(e) => set(k, e.target.value)} disabled={ro} placeholder={f.defaults?.[k] || ''} maxLength={k === 'address' ? 500 : 200} /></label>
            ))}
            <div className="field ws-full"><span className="field-label">{t('ws.lh.f.footerText')}</span>
              <RichTextEditor id="lh-footer" full docs blocks={false} disabled={ro} value={f.footerText || ''} onChange={(v) => set('footerText', v)} placeholder={t('ws.lh.footerPh')} /></div>
            <label className="field"><span className="field-label">{t('ws.lh.f.accent')}</span>
              <span className="ws-row"><input type="color" value={f.accent || '#c8102e'} onChange={(e) => set('accent', e.target.value)} disabled={ro} aria-label={t('ws.lh.f.accent')} /><code className="small">{f.accent}</code></span></label>
          </div>
          <p className="ws-hint">{t('ws.lh.defaultsHint')}</p>
          {/* Signatures are personal (My signature), never the company's (migration 044). */}
          <p className="ws-hint">{t('ws.lh.signaturePersonal')} <Link to="/account/signature">{t('sig.myTitle')}</Link></p>
          <label className="field-checkbox"><input type="checkbox" checked={!!f.approvalRequired} disabled={ro} onChange={(e) => set('approvalRequired', e.target.checked)} /><span>{t('ws.lh.approval')}</span></label>
          <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>
          <div className="ws-actions">
            <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={() => (ro ? pdfBlobUrl('/workspace/doc-library/preview', { kind: 'quotation', lang, title: t('ws.lh.previewTitle'), bodyHtml: sample, hasItems: true }).then(setPreview) : save(true))}>{t('ws.lh.preview')}</button>
            {!ro && <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => save(false)}>{busy === 'save' ? t('common.saving') : t('common.save')}</button>}
          </div>
        </section>
        <section className="ws-card">
          <h2>{t('ws.lh.previewTitle')}</h2>
          {preview ? <PdfFrame url={preview} title={t('ws.lh.previewTitle')} /> : <p className="muted small">{t('ws.lh.previewHint')}</p>}
        </section>
      </div>
      <StampsSection />
      <SignaturesSection />
    </div>
  );
}

// A private image (stamp, logo) shown through the API (never a public URL).
export function usePrivateImage(path) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) { setUrl(null); return undefined; }
    let gone = false; let u = null;
    fetch(`/api${path}`, { headers: { Authorization: `Bearer ${getToken()}` } }).then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b && !gone) { u = URL.createObjectURL(b); setUrl(u); } }).catch(() => {});
    return () => { gone = true; if (u) URL.revokeObjectURL(u); };
  }, [path]);
  return url;
}
export function StampImage({ id, alt = '', className = 'ws-stamp-img' }) {
  const url = usePrivateImage(id ? `/workspace/doc-stamps/${id}/image` : null);
  return url ? <img src={url} alt={alt} className={className} /> : <span className={`${className} ws-stamp-ph`} aria-hidden="true" />;
}

// "Cachet de l'entreprise / Company stamp": several stamps, each with a name.
function StampsSection() {
  const { t, errorText } = useI18n();
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [edit, setEdit] = useState(null);
  const [msg, setMsg] = useState({ kind: '', text: '' });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api('/workspace/doc-stamps').then(setData).catch((e) => setMsg({ kind: 'error', text: errorText(e) })), [errorText]);
  useEffect(() => { load(); }, [load]);
  async function add(e) {
    e.preventDefault();
    if (!name.trim() || !file) { setMsg({ kind: 'error', text: t(!name.trim() ? 'errors.stamp_name_required' : 'errors.missing_file') }); return; }
    if (!['image/png', 'image/jpeg'].includes(file.type)) { setMsg({ kind: 'error', text: t('errors.invalid_image') }); return; }
    const fd = new FormData(); fd.append('name', name.trim()); fd.append('file', file);
    setBusy(true);
    try { await api('/workspace/doc-stamps', { method: 'POST', form: fd }); setName(''); setFile(null); e.target.reset(); setMsg({ kind: 'success', text: t('ws.stamp.added') }); load(); }
    catch (err) { setMsg({ kind: 'error', text: errorText(err) }); } finally { setBusy(false); }
  }
  async function rename(s) {
    try { await api(`/workspace/doc-stamps/${s.id}`, { method: 'PATCH', body: { name: edit.name } }); setEdit(null); load(); } catch (err) { setMsg({ kind: 'error', text: errorText(err) }); }
  }
  async function remove(s) {
    if (!window.confirm(t('ws.stamp.deleteConfirm', { name: s.name }))) return;
    try { await api(`/workspace/doc-stamps/${s.id}`, { method: 'DELETE' }); load(); } catch (err) { setMsg({ kind: 'error', text: errorText(err) }); }
  }
  if (!data) return msg.text ? <Alert kind="error">{msg.text}</Alert> : null;
  return (
    <section className="ws-card ws-stamps">
      <h2>{t('ws.stamp.title')}</h2>
      <p className="ws-hint">{t('ws.stamp.lead')}</p>
      {data.stamps.length ? (
        <ul className="ws-stamp-list">
          {data.stamps.map((s) => (
            <li key={s.id}>
              <StampImage id={s.id} alt={s.name} />
              {edit?.id === s.id ? (
                <span className="ws-row"><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} maxLength={80} aria-label={t('ws.stamp.name')} />
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => rename(s)}>{t('common.save')}</button><button type="button" className="btn btn-link btn-sm" onClick={() => setEdit(null)}>{t('common.cancel')}</button></span>
              ) : <strong>{s.name}</strong>}
              {data.canManage && edit?.id !== s.id && (
                <span className="ws-row">
                  <button type="button" className="btn btn-link btn-sm" onClick={() => setEdit({ id: s.id, name: s.name })}>{t('ws.stamp.rename')}</button>
                  <button type="button" className="btn btn-link btn-sm is-danger" onClick={() => remove(s)}>{t('ws.lh.remove')}</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : <p className="muted small">{t('ws.stamp.none')}</p>}
      {data.canManage && (
        <form className="ws-form ws-stamp-add" onSubmit={add}>
          <label className="field"><span className="field-label">{t('ws.stamp.name')}</span><input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t('ws.stamp.namePh')} /></label>
          <label className="field"><span className="field-label">{t('ws.stamp.file')}</span><input type="file" accept="image/png,image/jpeg" onChange={(e) => setFile(e.target.files[0] || null)} /></label>
          <p className="ws-hint ws-full">{t('ws.stamp.fileHint')}</p>
          <div className="ws-actions ws-full"><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? t('common.loading') : `+ ${t('ws.stamp.add')}`}</button></div>
        </form>
      )}
      {!data.canUse && <p className="ws-hint">{t('ws.stamp.noRight')}</p>}
      <Alert kind={msg.kind || 'error'}>{msg.text}</Alert>
    </section>
  );
}
